import { Control, Controller } from "react-hook-form";
import { DynamicFee, SalaryDeductionType } from "../../../__generated__/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import Gap from "../../../ui_atoms/Gap";
import { BodyMediumText, BodySmallText } from "../../FormattedText";
import { FormData, useValidateNumber } from "./PayrollBreakdownEditor";
import TextField from "../../TextField";
import { useTheme } from "../../../hooks/use-theme";
import { formatCurrency, getAmountWithClientDeductions } from "../../../utils";
import FullWidthLine from "../../FullWidthLine";
import VStack from "../../../ui_atoms/VStack";
import { ChainTextInputProps } from "../../../hooks/use-chain-input-focus";

type DynamicFeesProps = {
    clientDeductions: SalaryDeductionType[];
    dynamicFees: DynamicFee[];
    control: Control<FormData>;
    chainInputFocus?: (index: number) => ChainTextInputProps;
};

export const DynamicFees = ({
    dynamicFees,
    clientDeductions,
    control,
    chainInputFocus = () => ({}),
}: DynamicFeesProps) => {
    const validateNumber = useValidateNumber();
    const i18n = useI18n();
    const { colors } = useTheme();

    return (
        dynamicFees.length > 0 && (
            <>
                <Gap gap={1} />
                <VStack gap={1}>
                    {dynamicFees?.map((fee, index) => (
                        <>
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.field_variable_fee_count", {
                                    feeName:
                                        fee.fee.otherTitle ||
                                        i18n.t(`fees.type.${fee.fee.type}.title`),
                                })}
                            </BodyMediumText>
                            <Controller
                                name={`dynamicFees.${index}`}
                                control={control}
                                rules={{
                                    validate: {
                                        isNumber: (v) =>
                                            !v.count || validateNumber(v.count?.toString()),
                                    },
                                }}
                                render={({ field: { value, onChange, onBlur } }) => {
                                    return (
                                        <TextField
                                            {...chainInputFocus(index)}
                                            onBlur={onBlur}
                                            onChangeText={(v) => {
                                                const parsedValue = parseFloat(v);

                                                onChange({
                                                    ...value,
                                                    count: !isNaN(parsedValue) ? parsedValue : "",
                                                });
                                            }}
                                            value={value.count?.toString() || ""}
                                            isDecimalNumber
                                        />
                                    );
                                }}
                            />
                            <Gap gap={1 / 3} />
                            <BodySmallText color={colors.greyAccessible}>
                                {i18n.t("payroll_breakdown.field_variable_fee_count_info", {
                                    feeCount: formatCurrency({
                                        amountInCents: fee.fee.amount,
                                        displayDigitsWhenNoCents: false,
                                    }),
                                    withTaxes: formatCurrency({
                                        amountInCents: getAmountWithClientDeductions(
                                            fee.fee.amount,
                                            clientDeductions,
                                        ),
                                        displayDigitsWhenNoCents: false,
                                    }),
                                })}
                            </BodySmallText>
                        </>
                    ))}
                </VStack>
                <Gap gap={1} />
                <FullWidthLine />
            </>
        )
    );
};
